import React from "react";
import {autobind} from "core-decorators";
import {Button, message, Popconfirm, Tooltip} from "antd";
import {FaFolderOpen, FaHeart, FaRegHeart, FaTrash} from "react-icons/fa";
import {action, runInAction} from "mobx";
import {inject, observer} from "mobx-react";
import {Link} from "react-router-dom";
import {gql} from "@apollo/client";

import "./RehearsalsActions.scss";
import {DeleteRehearsal, RehearsalsQuery, ToggleRehearsalFavorite} from "@/graphql/types";
import {paths} from "@/routes";
import {colors} from "@/styles/colors";
import DownloadButton from "@/components/DownloadButton/DownloadButton";
import Store from "@/stores";

export interface IRehearsalsActionsProps {
  rehearsal: RehearsalsQuery["rehearsals"][0];
  rehearsals: RehearsalsQuery["rehearsals"];
  store?: Store;
}

@inject("store")
@autobind
@observer
export default class RehearsalsActions extends React.Component<IRehearsalsActionsProps> {
  async delete() {
    const {rehearsal, rehearsals} = this.props;
    const rehearsalIdx = rehearsals.indexOf(rehearsal);
    runInAction(() => rehearsals.splice(rehearsalIdx, 1)); // optimistic UI update
    try {
      const result = await this.props.store!.apollo.client.mutate<DeleteRehearsal>({
        mutation: gql`
          mutation DeleteRehearsal($id: Int!) {
            rehearsal: delete_rehearsals_by_pk(id: $id) {
              id
            }
          }
        `,
        variables: {id: rehearsal.id},
      });
      const deletedRehearsal = result.data?.rehearsal;
      if (!deletedRehearsal) throw new Error("Could not delete Rehearsal");
    } catch (error) {
      runInAction(() => rehearsals.splice(rehearsalIdx, 0, rehearsal));
      console.error(error);
      message.error("Oops! Error while deleting rehearsal");
    }
  }

  @action
  setFavorite(favorite: boolean) {
    const {rehearsal} = this.props;
    rehearsal.favorite = favorite;
  }

  async toggleFavorite() {
    const {rehearsal} = this.props;
    const favorite = rehearsal.favorite;
    this.setFavorite(!favorite); // optimistic UI update
    try {
      const result = await this.props.store!.apollo.client.mutate<ToggleRehearsalFavorite>({
        mutation: gql`
          mutation ToggleRehearsalFavorite($id: Int!, $favorite: Boolean!) {
            rehearsal: update_rehearsals_by_pk(pk_columns: {id: $id}, _set: {favorite: $favorite}) {
              id
              favorite
            }
          }
        `,
        variables: {
          id: rehearsal.id,
          favorite: !favorite,
        },
      });
      const updatedRehearsal = result.data?.rehearsal;
      if (!updatedRehearsal) throw new Error("Could not update Rehearsal's favorite");
      this.setFavorite(updatedRehearsal.favorite);
    } catch (error) {
      this.setFavorite(favorite);
      console.error(error);
      message.error("Oops! Error while changing the favorite status");
    }
  }

  public render() {
    const {rehearsal} = this.props;
    const {piece} = rehearsal.part;
    return (
      <div className="RehearsalsActions">
        <Tooltip title={piece.deleted ? "Open (piece is deleted)" : "Open"} placement="topLeft" arrowPointAtCenter>
          <Link to={paths.piece(piece.id, {rehearsals: rehearsal.id})}>
            <Button type="link" shape="circle" size="small" disabled={piece.deleted}>
              <FaFolderOpen />
            </Button>
          </Link>
        </Tooltip>
        <Tooltip title={rehearsal.favorite ? "Unfavorite" : "Favorite"} placement="topLeft" arrowPointAtCenter>
          <Button type="link" shape="circle" size="small" onClick={this.toggleFavorite}>
            {rehearsal.favorite ? <FaHeart color={colors.red} /> : <FaRegHeart />}
          </Button>
        </Tooltip>
        <Tooltip title="Download" placement="topLeft" arrowPointAtCenter>
          <DownloadButton url={rehearsal.audio} filename={rehearsal.part.name} />
        </Tooltip>
        <Tooltip title="Delete" placement="topLeft" arrowPointAtCenter>
          <Popconfirm
            title="Are you sure you want to delete this rehearsal?"
            placement="topRight"
            arrowPointAtCenter
            onConfirm={this.delete}
          >
            <Button type="link" shape="circle" size="small">
              <FaTrash />
            </Button>
          </Popconfirm>
        </Tooltip>
      </div>
    );
  }
}
